import { useCallback, useEffect, useRef, useState } from "react";
import { Activity, CalendarClock, Cpu, Coins, Network, RefreshCcw, ScrollText, Server, Users, Wifi } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, rupiah } from "../lib/api";
import { useRouters } from "../context/RouterContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const mib = (b) => `${(Number(b || 0) / 1048576).toFixed(2)} MiB`;
const fmtBps = (b) => (b >= 1e6 ? `${(b / 1e6).toFixed(2)} Mbps` : `${(b / 1e3).toFixed(2)} kbps`);

export default function RouterBoard() {
  const { selected, router } = useRouters();
  const [t, setT] = useState(null);
  const [traffic, setTraffic] = useState([]);
  const [trafficError, setTrafficError] = useState("");
  const [iface, setIface] = useState("");
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const ifaceRef = useRef("");
  const trafficFails = useRef(0);

  const loadTelemetry = useCallback(async () => {
    if (!selected) return;
    try {
      const { data } = await api.get(`/routers/${selected}/telemetry`);
      setT(data);
      setError("");
      if (data.interfaces?.length) {
        ifaceRef.current = data.interfaces[0];
        setIface(data.interfaces[0]);
      }
    } catch (e) {
      setError(errMsg(e));
      setT(null);
    } finally {
      setLoading(false);
    }
  }, [selected]);

  const loadTraffic = useCallback(async () => {
    if (!selected || !ifaceRef.current) return; // tunggu telemetry menemukan interface aktif
    try {
      const { data } = await api.get(`/routers/${selected}/traffic`, { params: { interface: ifaceRef.current } });
      trafficFails.current = 0;
      setTrafficError("");
      setTraffic((prev) => [...prev.slice(-29), data]);
    } catch (e) {
      trafficFails.current += 1;
      if (trafficFails.current >= 3) setTrafficError("Traffic tidak tersedia untuk interface ini");
    }
  }, [selected]);

  const loadLogs = useCallback(async () => {
    if (!selected) return;
    try {
      const { data } = await api.get(`/routers/${selected}/logs`);
      setLogs(data);
    } catch (e) { /* abaikan */ }
  }, [selected]);

  useEffect(() => {
    setLoading(true);
    setTraffic([]);
    setLogs([]);
    setTrafficError("");
    trafficFails.current = 0;
    ifaceRef.current = "";
    setIface("");
    loadTelemetry();
    const i1 = setInterval(loadTelemetry, 10000);
    const i2 = setInterval(loadTraffic, 5000);
    const i3 = setInterval(loadLogs, 15000);
    const tTraffic = setTimeout(loadTraffic, 1200);
    const tLogs = setTimeout(loadLogs, 1500);
    return () => { clearInterval(i1); clearInterval(i2); clearInterval(i3); clearTimeout(tTraffic); clearTimeout(tLogs); };
  }, [loadTelemetry, loadTraffic, loadLogs]);

  if (!selected) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid="routerboard-empty">
        <Server className="w-10 h-10 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Pilih router terlebih dahulu dari dropdown di atas.</p>
      </div>
    );
  }

  const tiles = t ? [
    { label: "Hotspot Users", value: t.hotspot_users, sub: `${t.hotspot_active} aktif`, icon: Wifi, bg: "bg-blue-600", testid: "rb-tile-users" },
    { label: "PPP Secrets", value: t.ppp_secrets, sub: `${t.ppp_active} aktif`, icon: Network, bg: "bg-emerald-500", testid: "rb-tile-ppp" },
    { label: "Income Hari Ini", value: rupiah(t.income_today), sub: `Bulan ini ${rupiah(t.income_month)}`, icon: Coins, bg: "bg-rose-500", testid: "rb-tile-income" },
    { label: "Generate Terakhir", value: t.last_batch?.batch_id || "-", sub: t.last_batch ? `${t.last_batch.count} voucher • ${t.last_batch.profile}` : "belum ada", icon: Activity, bg: "bg-amber-500", testid: "rb-tile-lastgen" },
  ] : [];

  return (
    <div className="space-y-5" data-testid="routerboard-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Routerboard{router?.name ? ` — ${router.name}` : ""}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t ? `${t.board} • ROS ${t.version} • Uptime ${t.uptime}` : "Memuat info router..."}</p>
        </div>
        <Button variant="outline" onClick={() => { loadTelemetry(); loadLogs(); toast.success("Data router dimuat ulang"); }} data-testid="rb-btn-refresh">
          <RefreshCcw className="w-4 h-4 mr-1.5" /> Muat Ulang
        </Button>
      </div>

      {error && (
        <Card className="bg-rose-500/5 border-rose-500/20" data-testid="rb-error">
          <CardContent className="p-4 text-sm text-rose-500">{error}</CardContent>
        </Card>
      )}
      {loading && !error && <div className="text-sm text-muted-foreground py-8 text-center" data-testid="rb-loading">Menghubungi router...</div>}

      {t && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card className="bg-card border-border shadow-sm" data-testid="rb-card-clock">
              <CardContent className="p-4 flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-600/10 text-blue-600 flex items-center justify-center shrink-0"><CalendarClock className="w-5 h-5" /></div>
                <div className="text-xs space-y-1 font-mono">
                  <div>Date: <span className="font-semibold">{t.date}</span></div>
                  <div>Time: <span className="font-semibold">{t.time}</span></div>
                  <div>Uptime: <span className="font-semibold">{t.uptime}</span></div>
                  <div>Time Zone: <span className="font-semibold">{t.timezone}</span></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border shadow-sm" data-testid="rb-card-board">
              <CardContent className="p-4 flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-sky-500/10 text-sky-600 flex items-center justify-center shrink-0"><Server className="w-5 h-5" /></div>
                <div className="text-xs space-y-1 font-mono">
                  <div>Board Name: <span className="font-semibold">{t.board}</span></div>
                  <div>Router OS: <span className="font-semibold">{t.version}</span></div>
                  <div>Architecture: <span className="font-semibold">{t.arch}</span></div>
                  <div>Platform: <span className="font-semibold">{t.platform}</span></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border shadow-sm" data-testid="rb-card-resource">
              <CardContent className="p-4 flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center shrink-0"><Cpu className="w-5 h-5" /></div>
                <div className="text-xs space-y-1 font-mono">
                  <div>CPU Load: <span className="font-semibold">{t.cpu_load}%</span></div>
                  <div>Free Memory: <span className="font-semibold">{mib(t.free_memory)}</span></div>
                  <div>Free HDD: <span className="font-semibold">{mib(t.free_hdd)}</span></div>
                  {(t.voltage || t.temperature) && (
                    <div>{t.voltage ? `Voltage: ${t.voltage}V` : ""} {t.temperature ? `Temp: ${t.temperature}°C` : ""}</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {tiles.map((k) => (
              <Card key={k.label} className={`${k.bg} text-white border-0 shadow-md`} data-testid={k.testid}>
                <CardContent className="p-4 flex items-center gap-3">
                  <k.icon className="w-7 h-7 opacity-80 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-white/75">{k.label}</div>
                    <div className="text-lg font-extrabold font-mono truncate">{k.value}</div>
                    <div className="text-[11px] text-white/75 truncate">{k.sub}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Card className="lg:col-span-2 bg-card border-border shadow-sm" data-testid="rb-traffic-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading">Traffic — {iface || "mendeteksi interface..."}</CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                {traffic.length > 1 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={traffic} margin={{ left: 0, right: 8, top: 8 }}>
                      <defs>
                        <linearGradient id="gTx" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0B63E5" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#0B63E5" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gRx" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#F43F5E" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#F43F5E" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="ts" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtBps} stroke="hsl(var(--muted-foreground))" width={70} />
                      <Tooltip formatter={(v) => fmtBps(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Area type="monotone" dataKey="tx_bps" name="Tx" stroke="#0B63E5" fill="url(#gTx)" strokeWidth={2} isAnimationActive={false} />
                      <Area type="monotone" dataKey="rx_bps" name="Rx" stroke="#F43F5E" fill="url(#gRx)" strokeWidth={2} isAnimationActive={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-muted-foreground" data-testid="rb-traffic-loading">{trafficError || "Mengambil sampel traffic..."}</div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-sm" data-testid="rb-log-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2"><ScrollText className="w-4 h-4 text-primary" /> Hotspot Log</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-64 overflow-y-auto divide-y divide-border" data-testid="rb-log-table">
                  {logs.map((l, i) => (
                    <div key={i} className="px-4 py-2 text-xs">
                      <div className="flex gap-2">
                        <span className="font-mono text-muted-foreground shrink-0">{l.time}</span>
                        <span className="font-medium break-all">{l.message}</span>
                      </div>
                    </div>
                  ))}
                  {!logs.length && <div className="px-4 py-8 text-center text-xs text-muted-foreground">Belum ada log hotspot</div>}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
